import React, { Component } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import Tooltip from '../Tooltip/Tooltip';
import tooltipIcon from '../../images/tooltip.svg';

import { displayPercent } from '../../utils/Format/Format';

import crossIcon from '../../images/cross-icon.svg';

import styles from './Table.css';

/**
 Generic Table component
 Provide labels and data and render

 @note perhaps use a  more generic data structure
 such as :

 ```jsx
 <Table
 headers={["Field 1", "Field 2", "Field 3"]}
 rows={[[1,2,3], [4,5,6], [7,8,9]]}
 />
 ```
 */
class Table extends Component {
    static propTypes = {
        /**
         * Data to populate the table elements
         */
        data: PropTypes.shape({
            asOfDate: PropTypes.string,
            data: PropTypes.instanceOf(Array),
            title: PropTypes.string,
            headers: PropTypes.arrayOf(PropTypes.string),
            definition: PropTypes.string,
            labelSup: PropTypes.string,
            labelSubtitle: PropTypes.string,
            showDefinition: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.bool
            ]),
            disableTooltip: PropTypes.bool
        }),
        /**
         * renderProp to pass in a custom component to a table element
         */
        render: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
        /**
         * boolean to indicate if the rows should be styled with filled in bars
         */
        rowBars: PropTypes.bool,
        /**
         * boolean to indicate if the table header should be bolded
         */
        boldTableHeader: PropTypes.bool,
        /**
         * boolean to indicate if the table header bottom border should be hidden
         */
        hideTableHeaderBorder: PropTypes.bool,
        /**
         * Render function for table labels
         */
        renderLabel: PropTypes.func,
        /**
         * Flag to render geographical data chart
         */
        geoTable: PropTypes.bool,
        /**
         * custom class name to prepend the table container class
         */
        customClassName: PropTypes.string
    };

    static defaultProps = {
        data: {
            asOfDate: '',
            data: [],
            title: '',
            headers: [],
            definition: '',
            labelSup: '',
            labelSubtitle: '',
            showDefinition: ''
        },
        render: '',
        rowBars: false,
        geoTable: false,
        boldTableHeader: false,
        hideTableHeaderBorder: false,
        customClassName: '',
        renderLabel: () => {}
    };

    /* eslint-disable */
    constructor(props) {
        super(props);
        const { data } = this.props;
        this.state = {
            data: data
        };
    }

    hideDefinition = (i) => {
        const list = [...this.state.data.data];
        list[i].showDefinition = false;
        this.setState({ list });
    };

    showDefinition = (i) => {
        const list = [...this.state.data.data];
        list[i].showDefinition = true;
        this.setState({ list });
    };

    render() {
        const {
            asOfDate,
            headers,
            data,
            rowBars,
            boldTableHeader,
            hideTableHeaderBorder,
            renderLabel,
            render,
            geoTable,
            customClassName,
            displayTooltip,
            labelSpecificTooltips
        } = this.props;
        return (
            <div
                className={
                    customClassName
                        ? `${customClassName} tableContainer`
                        : 'tableContainer'
                }
            >
                <div className="table-header">
                    {data.title && <h2>{data.title}</h2>}
                    {asOfDate && <div>As of {data.asOfDate}</div>}
                </div>
                <table
                    className={cn({
                        'spaced-rows': rowBars,
                        'top-border': !headers
                    })}
                >
                    {data.headers && (
                        <thead>
                            <tr
                                className={`${hideTableHeaderBorder &&
                                    'no-bottom-border'}`}
                            >
                                {data.headers.map((h) => (
                                    <th
                                        key={`header-${h}`}
                                        className="t-header"
                                    >
                                        {boldTableHeader ? (
                                            <strong>{h}</strong>
                                        ) : (
                                            h
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                    )}
                    <tbody>
                        {data.data.map((ele, i) => (
                            <tr
                                className={cn(
                                    `row-${i}`,
                                    {
                                        'row-filled': rowBars
                                    },
                                    !ele.disableTooltip ? '' : 'no-click'
                                )}
                                id={ele.id ? ele.id : null}
                                key={`${ele.label}-${i}`}
                            >
                                {!geoTable &&
                                    (rowBars && ele.showDefinition === false ? (
                                        <td>
                                            {typeof ele.label === 'object' &&
                                            typeof renderLabel ===
                                                'function' ? (
                                                <div
                                                    className="show-def"
                                                    onClick={() =>
                                                        this.showDefinition(i)
                                                    }
                                                >
                                                    {renderLabel(ele.label)}
                                                    {ele.labelSup && (
                                                        <sup className="label-superscript">
                                                            {ele.labelSup}
                                                        </sup>
                                                    )}
                                                </div>
                                            ) : (
                                                <div
                                                    className="show-def"
                                                    onClick={() =>
                                                        this.showDefinition(i)
                                                    }
                                                >
                                                    {ele.label}
                                                    {ele.labelSup && (
                                                        <sup className="label-superscript">
                                                            {ele.labelSup}
                                                        </sup>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                    ) : (
                                        <td>
                                            {typeof ele.label === 'object' &&
                                            typeof renderLabel ===
                                                'function' ? (
                                                <div>
                                                    {renderLabel(ele.label)}
                                                    {ele.labelSup && (
                                                        <sup className="label-superscript">
                                                            {ele.labelSup}
                                                        </sup>
                                                    )}
                                                </div>
                                            ) : (
                                                <div>
                                                    {displayTooltip &&
                                                    Boolean(ele.tipContent) ? (
                                                        <Tooltip
                                                            title={
                                                                <span className="etf-aatr-tip-title">
                                                                    {ele.label}
                                                                </span>
                                                            }
                                                            content={
                                                                <div className="etf-aatr-tip-content">
                                                                    {
                                                                        ele.tipContent
                                                                    }
                                                                </div>
                                                            }
                                                        >
                                                            {ele.label}
                                                            <img
                                                                className="tooltip-img"
                                                                src={
                                                                    tooltipIcon
                                                                }
                                                                height="14px"
                                                                alt="Expense Limitations Tooltip"
                                                            />
                                                        </Tooltip>
                                                    ) : labelSpecificTooltips &&
                                                    (ele.label ===
                                                        'Premium/Discount to NAV' ||
                                                        ele.label ===
                                                            'Shares Outstanding') ? (
                                                        <Tooltip
                                                            title={
                                                                <span className="etf-aatr-tip-title">
                                                                    {ele.label}
                                                                </span>
                                                            }
                                                            content={
                                                                <div className="etf-aatr-tip-content">
                                                                    {
                                                                        ele.tipContent
                                                                    }
                                                                </div>
                                                            }
                                                        >
                                                            {ele.label}
                                                            <img
                                                                className="tooltip-img"
                                                                src={
                                                                    tooltipIcon
                                                                }
                                                                height="14px"
                                                                alt="Expense Limitations Tooltip"
                                                            />
                                                        </Tooltip>
                                                    ) : (
                                                        <React.Fragment>
                                                            {ele.labels ? (
                                                                <div className="label-parts">
                                                                    {ele.labels.map(
                                                                        (
                                                                            part,
                                                                            index
                                                                        ) => (
                                                                            <div
                                                                                className={`label-part ${index}`}
                                                                                key={
                                                                                    index
                                                                                }
                                                                            >
                                                                                {
                                                                                    part
                                                                                }
                                                                            </div>
                                                                        )
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                ele.label
                                                            )}
                                                            {ele.labelSup && (
                                                                <sup className="label-superscript">
                                                                    {
                                                                        ele.labelSup
                                                                    }
                                                                </sup>
                                                            )}
                                                            {ele.labelSubtitle && (
                                                                <div className="label-subtitle">
                                                                    {
                                                                        ele.labelSubtitle
                                                                    }
                                                                </div>
                                                            )}
                                                        </React.Fragment>
                                                    )}
                                                </div>
                                            )}

                                            {ele.definition && (
                                                <div className="t-definition">
                                                    <div>{ele.definition}</div>
                                                </div>
                                            )}
                                        </td>
                                    ))}
                                {!geoTable &&
                                    (rowBars &&
                                    ele.showDefinition === false &&
                                    ele.values ? (
                                        ele.values.map((val) =>
                                            Object.keys(val).map(() => (
                                                <td
                                                    key={`${
                                                        ele.label
                                                    }-${Object.keys(val)}-${i}`}
                                                >
                                                    {typeof render ===
                                                    'function'
                                                        ? render(
                                                              Object.values(
                                                                  val
                                                              )[0]
                                                          )
                                                        : Object.values(val)[0]}
                                                </td>
                                            ))
                                        )
                                    ) : rowBars && ele.showDefinition ? (
                                        <td valign="top">
                                            <img
                                                className="cross-icon"
                                                src={crossIcon}
                                                alt=""
                                                onClick={() =>
                                                    this.hideDefinition(i)
                                                }
                                            />
                                        </td>
                                    ) : (
                                        ele.values &&
                                        ele.values.map((val) =>
                                            Object.keys(val).map(() => (
                                                <td
                                                    key={`${
                                                        ele.label
                                                    }-${Object.keys(val)}-${i}`}
                                                >
                                                    {typeof render ===
                                                    'function'
                                                        ? render(
                                                              Object.values(
                                                                  val
                                                              )[0]
                                                          )
                                                        : Object.values(val)[0]}
                                                    {ele.tackFootnoteSymbol && (
                                                        <sup> †</sup>
                                                    )}
                                                    {ele.yieldSup && (
                                                        <sup className="label-superscript">
                                                            §
                                                        </sup>
                                                    )}
                                                </td>
                                            ))
                                        )
                                    ))}

                                {geoTable &&
                                    ele.name && (
                                        <td className="">
                                            <div className="geo-country-name">
                                                {ele.name}
                                            </div>
                                        </td>
                                    )}
                                {geoTable &&
                                    (ele.percentTotalNetAssets ? (
                                        <td>
                                            <div className="label-superscript">
                                                {displayPercent(
                                                    ele.percentTotalNetAssets
                                                )}
                                            </div>
                                        </td>
                                    ) : (
                                        <td>
                                            <div className="label-superscript">
                                                N/A
                                            </div>
                                        </td>
                                    ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
                <style jsx>{styles}</style>
            </div>
        );
    }
}

export default Table;
